import dayjs, { Dayjs } from 'dayjs'
import type { IcsDateObject, IcsEvent, IcsTimezone } from 'ts-ics'

import { getProfileTimezone } from '@/shared/composables/timezone'

const ICS_UNIX_EPOCH = new Date(Date.UTC(1970, 0, 1))

const toDayjs = (value: Dayjs | Date): Dayjs => dayjs(value)

export const formatIcsUtcOffset = (value: Dayjs | Date, timezone: string): string => (
  toDayjs(value).tz(timezone).format('ZZ')
)

/** Wall-clock in `timezone`, stored as UTC fields for ICS TZID serialization. */
export const toWallClockUtcDate = (value: Dayjs | Date, timezone: string): Date => {
  const local = toDayjs(value).tz(timezone)

  return new Date(Date.UTC(
    local.year(),
    local.month(),
    local.date(),
    local.hour(),
    local.minute(),
    local.second(),
  ))
}

export const toIcsDateTime = (
  value: Dayjs | Date,
  timezone = getProfileTimezone(),
): IcsDateObject => {
  const instant = toDayjs(value)

  return {
    date: instant.toDate(),
    type: 'DATE-TIME',
    local: {
      date: toWallClockUtcDate(instant, timezone),
      timezone,
      tzoffset: formatIcsUtcOffset(instant, timezone),
    },
  }
}

export const withIcsDateTimezone = (
  value: IcsDateObject,
  timezone = getProfileTimezone(),
): IcsDateObject => {
  if (value.type === 'DATE') {
    return value
  }

  return {
    ...value,
    ...toIcsDateTime(value.date, timezone),
  }
}

export const withEventTimezone = (
  event: IcsEvent,
  timezone = getProfileTimezone(),
): IcsEvent => {
  const next: IcsEvent = {
    ...event,
    start: withIcsDateTimezone(event.start, timezone),
  }

  if (event.end) {
    next.end = withIcsDateTimezone(event.end, timezone)
  }

  if (event.recurrenceRule?.until) {
    next.recurrenceRule = {
      ...event.recurrenceRule,
      until: withIcsDateTimezone(event.recurrenceRule.until, timezone),
    }
  }

  return next
}

export const buildIcsTimezone = (
  timezone = getProfileTimezone(),
  reference: Dayjs | Date = dayjs(),
): IcsTimezone => {
  const offset = formatIcsUtcOffset(reference, timezone)

  return {
    id: timezone,
    props: [{
      type: 'STANDARD',
      start: ICS_UNIX_EPOCH,
      offsetFrom: offset,
      offsetTo: offset,
    }],
  }
}
